import { useCallback, useState } from "react";
import {
  getMerchantPlanErrorMessage,
  upgradePlan as upgradePlanRequest,
} from "../MerchantPlan.api";
import { MERCHANT_PLAN_NOTIFICATION_MESSAGES } from "../MerchantPlan.const";

export type UpgradePlanType = "basic" | "premium";

export interface UpgradePlanResult {
  planType: UpgradePlanType;
  checkout_url: string;
  session_id?: string;
  status: "success";
}

const openCheckoutWindow = (checkoutUrl: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  const checkoutWindow = window.open(checkoutUrl, "noopener,noreferrer");
  if (!checkoutWindow) {
    throw new Error("Unable to open checkout window. Please allow popups and try again.");
  }

  const mainWindow = window;
  const popupMonitor = window.setInterval(() => {
    if (checkoutWindow.closed) {
      window.clearInterval(popupMonitor);
      mainWindow.focus();
    }
  }, 500);

  const handleCheckoutComplete = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      return;
    }

    if (event.data?.type === "STRIPE_CHECKOUT_COMPLETED") {
      window.clearInterval(popupMonitor);
      if (!checkoutWindow.closed) {
        checkoutWindow.close();
      }
      mainWindow.focus();
      window.removeEventListener("message", handleCheckoutComplete);
    }
  };

  window.addEventListener("message", handleCheckoutComplete);
};

export const useUpgradePlan = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upgrade = useCallback(async (planType: UpgradePlanType): Promise<UpgradePlanResult> => {
    if (isLoading) {
      throw new Error(MERCHANT_PLAN_NOTIFICATION_MESSAGES.UPGRADE_ERROR);
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await upgradePlanRequest(planType);
      openCheckoutWindow(response.checkout_url);

      return {
        planType,
        checkout_url: response.checkout_url,
        session_id: response.session_id,
        status: "success" as const,
      };
    } catch (requestError) {
      const message = getMerchantPlanErrorMessage(
        requestError,
        MERCHANT_PLAN_NOTIFICATION_MESSAGES.UPGRADE_ERROR,
      );
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  return { upgrade, isLoading, error };
};
